import {
  fetchConferenceAiForGoodByYear,
  fetchConferenceDynamicData,
  fetchConferenceExhibitionByYear,
  fetchConferenceHighlights,
  fetchConferenceMoments,
  fetchConferenceNewsByYear,
  fetchConferencePartnerList,
  fetchConferenceReportList,
  fetchConferenceTheme,
  fetchConferenceYearOptions,
  isConferenceRequestSuccessful,
  resolveConferenceCoreSectionYears,
} from './api';
import { buildConferenceSectionYearState } from './sectionYearState';
import type {
  ConferenceAgendaDetailResp,
  ConferenceAiForGoodListResp,
  ConferenceBrandItem,
  ConferenceDynamicData,
  ConferenceExhibitionListResp,
  ConferenceGuestGroupListResp,
  ConferenceHighlightItem,
  ConferenceMomentListResp,
  ConferenceNewsListResp,
  ConferenceSite,
  ConferenceThemeItem,
  ConferenceVideoListResp,
} from './types';

export interface ConferenceIndependentSectionStates {
  guests: ReturnType<
    typeof buildConferenceSectionYearState<ConferenceGuestGroupListResp>
  >;
  videos: ReturnType<
    typeof buildConferenceSectionYearState<ConferenceVideoListResp>
  >;
  agenda: ReturnType<
    typeof buildConferenceSectionYearState<ConferenceAgendaDetailResp>
  >;
  aiForGood: ReturnType<
    typeof buildConferenceSectionYearState<ConferenceAiForGoodListResp>
  >;
  exhibition: ReturnType<
    typeof buildConferenceSectionYearState<ConferenceExhibitionListResp>
  >;
  moments: ReturnType<
    typeof buildConferenceSectionYearState<ConferenceMomentListResp>
  >;
  news: ReturnType<
    typeof buildConferenceSectionYearState<ConferenceNewsListResp>
  >;
}

export interface ConferenceInitialSnapshot {
  selectedYear: number;
  yearOptions: number[];
  independentSectionsReady: boolean;
  independentSectionStates: ConferenceIndependentSectionStates;
  dynamicData: ConferenceDynamicData;
  momentsData?: ConferenceMomentListResp;
  themeData?: ConferenceThemeItem;
  highlightsData?: ConferenceHighlightItem[];
  brandsData: ConferenceBrandItem[];
  brandsLoaded: boolean;
  canReplaceSnapshot: boolean;
}

export type ConferenceInitialSnapshotInput =
  Partial<ConferenceInitialSnapshot> & {
    selectedYear?: number;
    dynamicData?: ConferenceDynamicData;
  };

const createEmptyIndependentSectionStates =
  (): ConferenceIndependentSectionStates => ({
    guests: { availableYears: [] },
    videos: { availableYears: [] },
    agenda: { availableYears: [] },
    aiForGood: { availableYears: [] },
    exhibition: { availableYears: [] },
    moments: { availableYears: [] },
    news: { availableYears: [] },
  });

const createEmptyAiForGoodResp = (): ConferenceAiForGoodListResp => ({
  list: [],
  total: 0,
});

const createEmptyConferenceAgendaResp = (): ConferenceAgendaDetailResp => ({
  list: [],
});

const createEmptyConferenceExhibitionResp =
  (): ConferenceExhibitionListResp => ({
    list: [],
    total: 0,
  });

const createEmptyConferenceMomentResp = (): ConferenceMomentListResp => ({
  momentList: [],
});

const createEmptyConferenceNewsResp = (): ConferenceNewsListResp => ({
  list: [],
  total: 0,
  page: 1,
  pageSize: 3,
});

export const createConferenceInitialSnapshot = (
  input: ConferenceInitialSnapshotInput = {}
): ConferenceInitialSnapshot => ({
  selectedYear: input.selectedYear || new Date().getFullYear(),
  yearOptions: input.yearOptions || [],
  independentSectionsReady: input.independentSectionsReady ?? true,
  independentSectionStates:
    input.independentSectionStates || createEmptyIndependentSectionStates(),
  dynamicData: input.dynamicData || {},
  momentsData: input.momentsData,
  themeData: input.themeData,
  highlightsData: input.highlightsData,
  brandsData: input.brandsData || [],
  brandsLoaded: input.brandsLoaded ?? false,
  canReplaceSnapshot: input.canReplaceSnapshot ?? true,
});

export const isSameConferenceData = <T,>(current: T, next: T) =>
  JSON.stringify(current) === JSON.stringify(next);

const hasConferenceListData = (value?: unknown) => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Object.values(value).some(
    (item) => Array.isArray(item) && item.length > 0
  );
};

export const hasConferenceSnapshotContent = (
  snapshot: ConferenceInitialSnapshot
) =>
  Boolean(
    snapshot.themeData ||
      snapshot.highlightsData?.length ||
      snapshot.brandsData.length ||
      hasConferenceListData(snapshot.momentsData) ||
      Object.values(snapshot.dynamicData).some(hasConferenceListData) ||
      Object.values(snapshot.independentSectionStates).some(
        (state) => state.availableYears.length > 0
      )
  );

export const mergeConferenceInitialSnapshot = (
  current: ConferenceInitialSnapshot,
  next?: ConferenceInitialSnapshot
) => {
  // 客户端复查返回相同业务数据时保留原引用，避免水合后产生无意义重渲染。
  if (!next || isSameConferenceData(current, next)) {
    return current;
  }
  return next.canReplaceSnapshot ? next : current;
};

export const fetchConferenceInitialSnapshot = async (
  site: ConferenceSite
): Promise<ConferenceInitialSnapshot> => {
  const [yearData, themeData, highlightsData, reportsData, partnersData] =
    await Promise.all([
    fetchConferenceYearOptions(site),
    fetchConferenceTheme(site),
    fetchConferenceHighlights(site),
    fetchConferenceReportList(site),
    fetchConferencePartnerList(site),
  ]);
  const selectedYear =
    yearData.DefaultYear || yearData.Years?.[0] || new Date().getFullYear();
  const yearOptions = yearData.Years?.length ? yearData.Years : [selectedYear];
  const resolvedSelectedYear = yearOptions.includes(selectedYear)
    ? selectedYear
    : yearOptions[0] || selectedYear;
  const aiForGoodYears = yearData.AiForGoodYears || [];
  const exhibitionYears = yearData.ExhibitionYears || [];
  const momentYears = yearData.MomentYears || [];
  const newsYears = yearData.NewsYears || [];

  const [nextData, momentsData, aiForGoodData, exhibitionData, newsData] =
    await Promise.all([
      fetchConferenceDynamicData({
        conferenceYear: resolvedSelectedYear,
        site,
        reportsData,
        partnersData,
      }),
      momentYears[0]
        ? fetchConferenceMoments({ conferenceYear: momentYears[0], site })
        : Promise.resolve(undefined),
      aiForGoodYears[0]
        ? fetchConferenceAiForGoodByYear({
            conferenceYear: aiForGoodYears[0],
            site,
          })
        : Promise.resolve(undefined),
      exhibitionYears[0]
        ? fetchConferenceExhibitionByYear({
            conferenceYear: exhibitionYears[0],
            site,
          })
        : Promise.resolve(undefined),
      newsYears[0]
        ? fetchConferenceNewsByYear({ conferenceYear: newsYears[0], site })
        : Promise.resolve(undefined),
    ]);

  const coreSectionYears = await resolveConferenceCoreSectionYears({
    guestYears: yearData.GuestYears,
    agendaYears: yearData.AgendaYears,
    videoYears: yearData.VideoYears,
    defaultYear: resolvedSelectedYear,
    defaultData: {
      guests: nextData.guests,
      agenda: nextData.agenda,
      videos: nextData.videos,
    },
    site,
  });
  const independentSectionStates: ConferenceIndependentSectionStates = {
    guests: buildConferenceSectionYearState({
      availableYears: coreSectionYears.guests.years,
      defaultYear: coreSectionYears.guests.years[0],
      defaultData: coreSectionYears.guests.dataByYear.get(
        coreSectionYears.guests.years[0]
      ),
    }),
    agenda: buildConferenceSectionYearState({
      availableYears: coreSectionYears.agenda.years,
      defaultYear: coreSectionYears.agenda.years[0],
      defaultData: coreSectionYears.agenda.dataByYear.get(
        coreSectionYears.agenda.years[0]
      ),
    }),
    videos: buildConferenceSectionYearState({
      availableYears: coreSectionYears.videos.years,
      defaultYear: coreSectionYears.videos.years[0],
      defaultData: coreSectionYears.videos.dataByYear.get(
        coreSectionYears.videos.years[0]
      ),
    }),
    aiForGood: buildConferenceSectionYearState({
      availableYears: aiForGoodYears,
      defaultYear: aiForGoodYears[0],
      defaultData: aiForGoodData,
    }),
    exhibition: buildConferenceSectionYearState({
      availableYears: exhibitionYears,
      defaultYear: exhibitionYears[0],
      defaultData: exhibitionData,
    }),
    moments: buildConferenceSectionYearState({
      availableYears: momentYears,
      defaultYear: momentYears[0],
      defaultData: momentsData,
    }),
    news: buildConferenceSectionYearState({
      availableYears: newsYears,
      defaultYear: newsYears[0],
      defaultData: newsData,
    }),
  };

  const requiredRequestData = [
    yearData,
    themeData,
    highlightsData,
    nextData.reports,
    nextData.partners,
    ...(momentYears[0] ? [momentsData] : []),
    ...(aiForGoodYears[0] ? [aiForGoodData] : []),
    ...(exhibitionYears[0] ? [exhibitionData] : []),
    ...(newsYears[0] ? [newsData] : []),
  ];
  const canReplaceSnapshot =
    requiredRequestData.every(isConferenceRequestSuccessful) &&
    coreSectionYears.guests.successful &&
    coreSectionYears.agenda.successful &&
    coreSectionYears.videos.successful;

  return createConferenceInitialSnapshot({
    selectedYear: resolvedSelectedYear,
    yearOptions,
    independentSectionStates,
    dynamicData: {
      ...nextData,
      guests: independentSectionStates.guests.defaultData || { list: [] },
      agenda:
        independentSectionStates.agenda.defaultData ||
        createEmptyConferenceAgendaResp(),
      videos: independentSectionStates.videos.defaultData || {
        list: [],
        total: 0,
        page: 1,
        pageSize: 100,
      },
      aiForGood:
        independentSectionStates.aiForGood.defaultData ||
        createEmptyAiForGoodResp(),
      exhibition:
        independentSectionStates.exhibition.defaultData ||
        createEmptyConferenceExhibitionResp(),
      news:
        independentSectionStates.news.defaultData ||
        createEmptyConferenceNewsResp(),
    },
    momentsData:
      independentSectionStates.moments.defaultData ||
      createEmptyConferenceMomentResp(),
    themeData,
    highlightsData,
    canReplaceSnapshot,
  });
};
